import React, { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionSubtitle,
  DefaultOption,
  Modal,
  RoutingRuleFooter,
  RoutingRuleTitle,
  handleErrorToast,
} from 'src/components/molecules';
import { GroupType, RoutingRuleType } from 'src/graphql';
import {
  useIsFeatureEnabled,
  useRoutingGroup,
  useWindowDimensions,
} from 'src/hooks';
import { FeatureFlag, FormState } from 'src/types';
import { Attributes, RoutingVariantType } from '../RoutingRuleAndPriority';
import { RoutingRuleBody } from '../RoutingRuleBody';
import { writeRoutingRule } from './helpers';
import {
  ReactStateHook,
  useMutateRoutingRule,
  useRoutingRuleDataSource,
  useRoutingRuleName,
  useRoutingRuleParameters,
} from './hooks';

interface Props {
  rule: RoutingRuleType;
  groups: GroupType[];
  index: number;
  defaultOpen?: boolean;
  attributes: Attributes;
  onClick?: () => void;
  recalculateCoordinates: () => void;
  tabIndex?: number; // Note - don't remove - this is passed in by the Accordion component
  onLeavePage?: (checkUnsavedRulesCallback: () => boolean) => void;
  saveRulesBeforeLeaving: boolean;
  setAllRulesAreSaved: ReactStateHook<boolean>;
  variant: RoutingVariantType;
}

export const RoutingRule = ({
  rule,
  groups,
  index,
  defaultOpen,
  attributes,
  onClick,
  recalculateCoordinates,
  onLeavePage,
  saveRulesBeforeLeaving,
  setAllRulesAreSaved,
  variant,
}: Props) => {
  const deleteModalState = {
    title: 'Delete rule?',
    description: 'This cannot be undone',
    onConfirm: () => deleteRule({ deleteRoutingRuleId: rule.id }),
    onCancel: () => setConfirmationModalOpen(false),
  };

  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [confirmationModalState, setConfirmationModalState] = useState<{
    title: string;
    description: string;
    onConfirm: () => void;
    onCancel?: () => void;
    confirmButtonText?: string;
  }>(deleteModalState);
  const [ruleState, setRuleState] = useState(rule);

  useEffect(() => {
    setRuleState((oldRule) => ({
      ...oldRule,
      priority: rule.priority,
    }));
  }, [rule.priority]);

  const {
    isNewName,
    hasUpdatedName,
    setHasUpdatedName,
    originalRuleName,
    setOriginalRuleName,
  } = useRoutingRuleName({ rule: ruleState });
  const { parameterList, setParameterList, parameterOptions } =
    useRoutingRuleParameters({
      rule: ruleState,
      variant,
    });
  const { selectedDataSource, setSelectedDataSource } =
    useRoutingRuleDataSource({ rule: ruleState, variant });
  const { groupDropdownState, selectedGroup } = useRoutingGroup({
    destinationGroup: ruleState.destinationGroup,
    disabled: ruleState.disabled,
    groups,
    variant,
  });

  const isRelationshipToOrgEnabled = useIsFeatureEnabled(
    FeatureFlag.RELATIONSHIP_TO_ORG,
  );

  const isCOIEnabled = useIsFeatureEnabled(FeatureFlag.CONFLICT_OF_INTEREST);

  const { saveRule, duplicateRule, enableRule, disableRule, deleteRule } =
    useMutateRoutingRule({
      setConfirmationModalOpen,
      setConfirmationModalState,
      rule: {
        id: rule.id,
        name: ruleState.name,
        destinationGroup: selectedGroup?.id,
        priority: ruleState.priority,
        selectedDataSource,
        parameterList,
        disabled: ruleState.disabled,
        groupAssignment: ruleState.groupAssignment ?? false,
      },
      onLeavePage,
      saveRulesBeforeLeaving,
      setAllRulesAreSaved,
      featureFlags: { isRelationshipToOrgEnabled },
    });

  const { width, height } = useWindowDimensions();

  const showError = groupDropdownState === FormState.ERROR;

  useEffect(() => {
    recalculateCoordinates();
  }, [showError, parameterList, width, height, recalculateCoordinates]);

  useEffect(() => {
    setRuleState((state) => ({
      ...state,
      rule: writeRoutingRule(parameterList, selectedDataSource, {
        isRelationshipToOrgEnabled,
      }),
    }));
  }, [selectedDataSource, parameterList, isRelationshipToOrgEnabled]);

  return (
    <>
      <Accordion
        title={
          <RoutingRuleTitle
            rule={rule}
            name={ruleState.name}
            index={index}
            updateRule={(name) => setRuleState({ ...ruleState, name })}
            onSave={() => {
              const hasNameChanged = ruleState.name !== originalRuleName;
              if (hasNameChanged) {
                saveRule({
                  parameters: {
                    id: rule.id,
                    destinationGroup: ruleState.destinationGroup,
                    disabled: ruleState.disabled,
                    rule: ruleState.rule,
                    priority: rule.priority,
                    name: ruleState.name,
                    groupAssignment: ruleState.groupAssignment ?? false,
                  },
                });
                setOriginalRuleName(rule.name);
              }
            }}
          />
        }
        subtitle={
          <AccordionSubtitle
            date={rule.createdAt}
            showError={showError}
            disabled={ruleState.disabled}
            author={rule.Author?.displayName || rule.Author?.name}
            errorMessage={'Rule error'}
          />
        }
        last={true}
        topBorder
        shadow
        topBorderRadius="12px"
        bottomBorderRadius="12px"
        defaultOpen={defaultOpen}
        onClick={onClick}
        data-testid={`routing-rule-accordion-${rule.id}`}
        openCallback={recalculateCoordinates}
        showError={showError}
      >
        <TabIndexContainer>
          <RoutingRuleBody
            rule={rule}
            selectedDataSource={selectedDataSource}
            setSelectedDataSource={setSelectedDataSource}
            groups={groups ?? []}
            selectedGroup={selectedGroup}
            updateRule={(option) =>
              setRuleState({
                ...ruleState,
                destinationGroup: (option as DefaultOption).value as string,
              })
            }
            groupDropdownState={groupDropdownState}
            parameterList={parameterList}
            parameterOptions={parameterOptions}
            setParameterList={setParameterList}
            attributes={attributes}
            groupAssignment={ruleState.groupAssignment ?? false}
            setGroupAssignment={(value) =>
              setRuleState({ ...ruleState, groupAssignment: value })
            }
            isCOIEnabled={isCOIEnabled}
            variant={variant}
          />
          <RoutingRuleFooter
            rule={ruleState}
            dropdownOptions={[
              ...(hasUpdatedName
                ? [{ label: 'Duplicate', value: 'duplicate' }]
                : []),
              { label: 'Delete', value: 'delete' },
            ]}
            duplicateRule={() => duplicateRule(rule)}
            deleteRule={() => {
              setConfirmationModalState(deleteModalState);
              setConfirmationModalOpen(true);
            }}
            saveRule={() => {
              if (isNewName) {
                setHasUpdatedName(true);
              }
              saveRule({
                parameters: {
                  id: rule.id,
                  destinationGroup: ruleState.destinationGroup,
                  disabled: ruleState.disabled,
                  rule: ruleState.rule,
                  priority: rule.priority,
                  name: ruleState.name,
                  groupAssignment: ruleState.groupAssignment ?? false,
                },
              });
            }}
            disableRule={() => {
              setRuleState({ ...ruleState, disabled: true });
              disableRule({ disableRuleId: rule.id });
            }}
            enableRule={() => {
              if (
                ruleState.destinationGroup &&
                Object.keys(ruleState.rule).length !== 0
              ) {
                setRuleState({ ...ruleState, disabled: false });
                enableRule({
                  parameters: {
                    id: rule.id,
                    destinationGroup: ruleState.destinationGroup,
                    disabled: false,
                    rule: ruleState.rule,
                    priority: rule.priority,
                    name: ruleState.name,
                    groupAssignment: ruleState.groupAssignment ?? false,
                  },
                });
              } else {
                handleErrorToast({
                  toastMessage:
                    'Can not enable rule without a destination group and at least one parameter',
                });
              }
            }}
          />
        </TabIndexContainer>
      </Accordion>
      <Modal
        size="medium"
        title={confirmationModalState.title}
        description={confirmationModalState.description}
        open={confirmationModalOpen}
        setOpen={setConfirmationModalOpen}
        onCancel={confirmationModalState.onCancel}
        onConfirm={confirmationModalState.onConfirm}
        confirmButtonText={confirmationModalState.confirmButtonText}
        data-testid="confirmation-modal"
      />
    </>
  );
};

const TabIndexContainer = ({
  tabIndex,
  children,
}: {
  tabIndex?: number;
  children: React.ReactNode;
}) => (
  <div>
    {React.Children.map(children, (child) => {
      if (React.isValidElement(child)) {
        return React.cloneElement(
          child as React.ReactElement<{ tabIndex?: number }>,
          {
            tabIndex,
          },
        );
      }

      return child;
    })}
  </div>
);
